import { getTheme, IIconProps, ITextFieldProps, mergeStyleSets, TextField } from '@fluentui/react'
import * as React from 'react'
import { FormEvent, ReactElement, useEffect, useState } from 'react'
import { Destination, IGraphResponse } from '../../../context/AxiosContext'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { AxiosError, AxiosRequestConfig } from 'axios'
import useAxios from '../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'

const theme = getTheme()

const classNames = mergeStyleSets({
    success: { color: theme.semanticColors.successIcon },
    error: { color: theme.semanticColors.errorIcon },
    new: { color: theme.semanticColors.warningIcon },
})

export enum FolderFieldStatus {
    LoadingInitial = 'LOADING_INITIAL',
    Loading = 'LOADING',
    Exists = 'EXISTS',
    New = 'NEW',
    Update = 'UPDATE',
    Error = 'ERROR',
}

const IconPropsMap: Map<FolderFieldStatus, IIconProps> = new Map([
    [FolderFieldStatus.Loading, { iconName: 'ProgressRingDots' }],
    [FolderFieldStatus.LoadingInitial, { iconName: 'ProgressRingDots' }],
    [FolderFieldStatus.Exists, { iconName: 'FabricFolderConfirm', styles: { root: classNames.success } }],
    [FolderFieldStatus.New, { iconName: 'FabricNewFolder', styles: { root: classNames.success } }],
    [FolderFieldStatus.Update, { iconName: 'FabricSyncFolder', styles: { root: classNames.success } }],
    [FolderFieldStatus.Error, { iconName: 'Error', styles: { root: classNames.error } }],
])

export const DRIVE_ITEM_CONFIG: AxiosRequestConfig = {
    baseURL: Destination.Documents,
    headers: { SuppressErrors: true },
    params: { $select: ['id', 'name', 'folder', 'parentReference'].join(',') },
}

type Props = {
    initialItemId?: string
    suggestedPath: string
    textFieldProps?: ITextFieldProps
    onChangePath?: (path: string, item?: DriveItem, errorMessage?: string) => void
}
export const getFullPath = (item: DriveItem) => {
    const search: string = '/root:'
    const parent: string = item.parentReference.path
    const substr: string = parent.substring(parent.indexOf(search) + search.length + 1)
    return (substr.length > 0 ? substr + '/' : '') + item.name
}

const SLASH_REGEX = /[\\/]+/g
const FORBIDDEN_CHARACTER_REGEX = /["*:<>?|]/g
const FORBIDDEN_START_END_CHAR_REGEX = /(^[-/.]+)|([-/.]+$)/g

export default function FolderField(props: Props): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const [status, setStatus] = useState<FolderFieldStatus>(FolderFieldStatus.LoadingInitial)
    const [path, setPath] = useState<string>(props.suggestedPath)
    const [errorMessage, setErrorMessage] = useState<string>()
    const [item, setItem] = useState<DriveItem>()
    const [initialItemId, setInitialItemId] = useState<string>()

    const updatePath = (path: string, formatPath: boolean = true) => {
        let prevFormattedPath = path
        let formattedPath = path
            // Replace one or more backslashes or forward slashes with a single forward slash
            ?.replace(SLASH_REGEX, '/')
            // Replace all forbidden characters in NTFS with a -, except / because this in this situation it serves as folder separator.
            ?.replace(FORBIDDEN_CHARACTER_REGEX, '-')
            // Cannot start or end with a '/', '-' or '.'
            ?.replace(FORBIDDEN_START_END_CHAR_REGEX, '')
        while (prevFormattedPath !== formattedPath) {
            //loop over the formattedPath until no illegal characters are found at the beginning or end.
            prevFormattedPath = formattedPath
            formattedPath = formattedPath?.replace(FORBIDDEN_START_END_CHAR_REGEX, '')
        }

        setStatus(FolderFieldStatus.Loading)
        setPath(formatPath ? formattedPath : path)
        setErrorMessage(undefined)

        // If the formatted path is empty, we know we won't be able to process it.
        if (formattedPath?.length > 0 && (formatPath || formattedPath === path)) {
            axios
                .get(`/root:/${formattedPath}`, DRIVE_ITEM_CONFIG)
                .then((response: IGraphResponse<DriveItem>) => {
                    if (initialItemId && response.data.id !== initialItemId) {
                        // If we already have a valid drive item, we will later attempt to rename it.
                        setErrorMessage(translate('file.edit.form.error.documentroot.exists', { path: formattedPath }))
                        setStatus(FolderFieldStatus.Error)
                    } else if (response.data.folder === undefined) {
                        // Always check that we're dealing with a folder.
                        setErrorMessage(translate('file.edit.form.error.documentroot.not_folder', { path: formattedPath }))
                        setStatus(FolderFieldStatus.Error)
                    } else {
                        // We found a folder that exists, but is free to use.
                        setStatus(FolderFieldStatus.Exists)
                        setItem(response.data)
                        setPath(getFullPath(response.data))
                    }
                })
                .catch((error: AxiosError) => {
                    if (error.response?.status !== 404) {
                        // Something else went wrong.
                        setErrorMessage(error.message)
                        setStatus(FolderFieldStatus.Error)
                        setItem(undefined)
                    } else if (initialItemId) {
                        // We will rename the current drive item.
                        setStatus(FolderFieldStatus.Update)
                    } else {
                        // We will create a new drive item.
                        setStatus(FolderFieldStatus.New)
                        setItem(undefined)
                    }
                })
        } else {
            setErrorMessage(translate('file.edit.form.error.documentroot.empty'))
            setStatus(FolderFieldStatus.Error)
        }
    }

    useEffect(() => {
        if (props.initialItemId) {
            setStatus(FolderFieldStatus.LoadingInitial)

            axios
                .get(`/items/${props.initialItemId}`, DRIVE_ITEM_CONFIG)
                .then((response: IGraphResponse<DriveItem>) => {
                    if (response.data.folder !== undefined) {
                        // We found a folder that exists, but is free to use.
                        setPath(getFullPath(response.data))
                        setItem(response.data)
                        setInitialItemId(props.initialItemId)
                        setStatus(FolderFieldStatus.Exists)
                    } else {
                        // Use the suggested path, because the initial drive item is not a folder.
                        updatePath(props.suggestedPath)
                    }
                })
                .catch(() => {
                    // Use the suggested path, because the initial drive item does not exist.
                    updatePath(props.suggestedPath)
                })
        } else {
            setStatus(FolderFieldStatus.Loading)
        }
    }, [props.initialItemId])

    useEffect(() => {
        if (status !== FolderFieldStatus.LoadingInitial) {
            // Replace all forbidden characters in NTFS with a -
            updatePath(props.suggestedPath?.replace(/["<>*:?|]/g, '-'))
        }
    }, [props.suggestedPath])

    useEffect(() => {
        if (![FolderFieldStatus.Loading, FolderFieldStatus.LoadingInitial].includes(status)) {
            // Callback to the parent component.
            props.onChangePath?.(path, item, errorMessage)
        } else if (status === FolderFieldStatus.Error && !initialItemId) {
            // Only set the item to undefined if we didn't initialize with a valid drive item.
            setItem(undefined)
        }
    }, [status])

    return (
        <TextField
            label={translate('file.edit.form.general.documentrootid')}
            {...props.textFieldProps}
            value={path || ''}
            prefix="/"
            onChange={(_: FormEvent, value?: string) => updatePath(value, false)}
            onBlur={() => updatePath(path)}
            iconProps={IconPropsMap.get(status)}
        />
    )
}
